/**
 * The bits of sorting a table that every table does the same way.
 *
 * Kept in one place so the register and the list of centres cannot drift into
 * two slightly different ideas of what clicking a column heading does.
 */
export type SortDirection = 'asc' | 'desc';

export interface SortState<Key extends string> {
  key: Key;
  direction: SortDirection;
}

/**
 * What clicking a heading should do.
 *
 * The same column again turns it around. A different column starts in the
 * direction its question is usually asked — biggest first for a count, newest
 * first for a date, A to Z for a name — so the first click answers what it was
 * clicked for instead of needing a second one to say so.
 */
export function nextSort<Key extends string>(
  current: SortState<Key>,
  key: Key,
  whenFresh: SortDirection,
): SortState<Key> {
  if (current.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: whenFresh };
}

/**
 * Folded for comparison: case, surrounding space, and the apostrophes.
 *
 * A name saved as "Shohruh’s" with a curly quote has to be findable by
 * somebody typing "Shohruh's" with the straight one their keyboard gives them,
 * and the same goes for the turned comma in "Ta’lim" and "O‘zbekiston".
 */
export function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[‘’ʻʼ՚′`´]/g, "'");
}

/** Just the digits, so a number matches however it was typed or stored. */
export const digits = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '');
